<template>
    <b-container fluid class="mail-editor-page">
        <!-- 헤더 -->
        <div class="border-bottom mb-3 pb-3 d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
                <b-button variant="outline-secondary" size="sm" class="mr-2" @click="goBack">← 목록</b-button>
                <h4 class="mb-0">{{ isEdit ? '이메일 템플릿 편집' : '새 이메일 템플릿' }}</h4>
            </div>
            <div class="d-flex align-items-center">
                <b-button
                    v-if="isEdit"
                    variant="outline-info"
                    size="sm"
                    class="mr-1"
                    :disabled="saving"
                    @click="openTestSend"
                >테스트 발송</b-button>
                <b-button
                    v-if="isEdit"
                    variant="outline-secondary"
                    size="sm"
                    class="mr-1"
                    :disabled="saving"
                    title="저장된 내용(모든 언어·발송 상황·조건)으로 새 템플릿 편집 화면을 엽니다. '생성'을 누르기 전에는 만들어지지 않습니다."
                    @click="duplicateThis"
                >이 템플릿 복제</b-button>
                <b-button
                    variant="outline-secondary"
                    size="sm"
                    class="mr-1"
                    :disabled="saving"
                    @click="goBack"
                >취소</b-button>
                <b-button
                    variant="primary"
                    size="sm"
                    class="mr-1"
                    :disabled="saving"
                    title="이름·발송 상황·조건·제목·본문을 함께 저장합니다."
                    @click="save"
                >
                    <b-spinner v-if="saving" small class="mr-1" />
                    {{ isEdit ? '저장' : '생성' }}
                </b-button>
                <b-dropdown
                    v-if="isEdit"
                    size="sm"
                    variant="outline-secondary"
                    right
                    no-caret
                    toggle-class="px-2"
                    :disabled="saving"
                >
                    <template #button-content>⋮</template>
                    <b-dropdown-item-button variant="danger" @click="deleteTemplate">
                        템플릿 삭제
                    </b-dropdown-item-button>
                </b-dropdown>
            </div>
        </div>

        <b-spinner v-if="loading" variant="primary" class="d-block mx-auto my-5" />

        <template v-else>
            <!-- 기본 설정 (가로 압축) -->
            <b-card class="mb-3">
                <h6 class="mb-3">기본 설정</h6>
                <b-row>
                    <b-col cols="12" md="4" class="mb-2 mb-md-0">
                        <b-form-group label="템플릿 이름" label-size="sm" class="mb-0">
                            <b-form-input v-model="form.label" size="sm" placeholder="예: SAQ 평가 안내" />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-2 mb-md-0">
                        <b-form-group label="발송 상황" label-size="sm" class="mb-0">
                            <b-form-select v-model="form.scenario_code" :options="scenarioOptions" size="sm" />
                            <small v-if="scenariosError" class="text-danger">
                                발송 상황 목록을 불러오지 못했습니다.
                                <b-link @click="loadScenarios">다시 시도</b-link>
                            </small>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <div class="d-flex align-items-baseline justify-content-between">
                            <label class="small font-weight-bold mb-1">조건</label>
                            <b-form-checkbox v-model="form.isFallback" class="small">
                                기본 (조건 없음)
                            </b-form-checkbox>
                        </div>
                        <SimpleContextBuilder
                            v-if="!form.isFallback"
                            ref="contextBuilder"
                            :value="form.contexts"
                            :is-fallback="form.isFallback"
                            @input="form.contexts = $event"
                        />
                    </b-col>
                </b-row>
            </b-card>

            <!-- 이메일 콘텐츠 -->
            <b-card>
                <div class="d-flex align-items-center justify-content-between mb-3">
                    <h6 class="mb-0">이메일 콘텐츠</h6>
                    <b-button
                        variant="outline-primary"
                        size="sm"
                        :title="`다른 템플릿의 ${currentLocaleLabel} 제목·본문만 이 탭에 가져옵니다.`"
                        @click="openCopyModal"
                    >
                        내용 가져오기 ({{ currentLocaleLabel }})
                    </b-button>
                </div>

                <b-tabs v-model="activeTabIndex" content-class="mt-3">
                    <b-tab
                        v-for="(content, idx) in form.contents"
                        :key="content.locale + idx"
                    >
                        <template #title>
                            {{ getLocaleLabel(content.locale) }}
                            <span
                                v-if="form.contents.length > 1"
                                class="ml-1 text-danger cursor-pointer"
                                @click.stop="removeContent(idx)"
                            >×</span>
                        </template>

                        <!-- 제목 (본문/조건 라벨과 동일한 raw label 패턴으로 통일 — b-form-group 은 legend 로 렌더돼 크기가 어긋남) -->
                        <label class="small font-weight-bold mb-1 d-block">제목</label>
                        <b-form-input
                            :ref="`subject_${idx}`"
                            v-model="content.subject"
                            size="sm"
                            class="mb-3"
                            placeholder="메일 제목을 입력하세요"
                            @focus="setActiveField('subject', idx)"
                        />

                        <!-- 본문 -->
                        <div class="d-flex align-items-baseline justify-content-between mb-1">
                            <label class="small font-weight-bold mb-0">본문</label>
                            <b-button
                                variant="link"
                                size="sm"
                                class="p-0 small text-decoration-none"
                                @click="toggleSourceMode(idx)"
                            >
                                {{ content._sourceMode ? '← 비주얼 편집으로' : 'HTML 직접 편집' }}
                            </b-button>
                        </div>

                        <!-- 비주얼(WYSIWYG) 편집 -->
                        <MailDocCanvas
                            v-if="!content._sourceMode"
                            :ref="`canvas_${idx}`"
                            :key="`canvas-${content.locale}-${canvasEpoch}`"
                            :value="content.body"
                            @input="onCanvasInput(idx, $event)"
                            @unsupported="onCanvasUnsupported(idx, $event)"
                        />

                        <!-- HTML 직접 편집: 상하 가변 분할 -->
                        <HtmlSplitEditor
                            v-else
                            :ref="`body_${idx}`"
                            v-model="content.body"
                            :refresh-key="previewRefreshKey"
                            placeholder="메일 본문 내용(HTML)을 입력하거나 '내용 가져오기'로 가져오세요"
                            @focus="setActiveField('body', idx)"
                        />

                        <!-- 변수 삽입 -->
                        <div class="mt-3 border-top pt-3">
                            <b-button
                                variant="link"
                                size="sm"
                                class="p-0 text-dark text-decoration-none"
                                @click="showVariables = !showVariables"
                            >
                                <span class="small mr-1">{{ showVariables ? '▼' : '▶' }}</span>
                                <strong class="small">변수 삽입</strong>
                                <span class="small text-muted ml-1">(받는 사람 이름·회사명 등 자동 치환)</span>
                            </b-button>
                            <div v-show="showVariables" class="mt-2">
                                <VariableHelper
                                    :variables="availableVariables"
                                    :error="variablesError"
                                    @insert="insertVariable"
                                    @retry="loadDefaultVariables"
                                />
                            </div>
                        </div>

                        <b-form-checkbox v-model="content.is_default" class="mt-3">
                            기본 언어
                        </b-form-checkbox>
                    </b-tab>

                    <!-- 언어 추가 탭 -->
                    <template #tabs-end>
                        <b-nav-item class="ml-1" @click.prevent="showAddLocale = true">
                            <span class="text-primary">+ 언어 추가</span>
                        </b-nav-item>
                    </template>
                </b-tabs>

                <!-- 언어 추가 모달 -->
                <b-modal v-model="showAddLocale" title="언어 추가" size="sm" ok-title="추가" cancel-title="취소" @ok="addLocale">
                    <b-form-select v-model="newLocale" :options="availableLocaleOptions" size="sm" />
                </b-modal>
            </b-card>
        </template>

        <!-- 내용 가져오기(현재 언어 탭만) 모달 -->
        <b-modal
            v-model="copyModal.show"
            size="xl"
            hide-footer
            scrollable
            :title="`내용 가져오기 — ${currentLocaleLabel} 탭`"
        >
            <p class="small text-muted">
                선택한 템플릿의 <strong>{{ currentLocaleLabel }} 제목·본문</strong>만 현재 탭에 가져옵니다.
                원본에 이 언어가 없으면 가져오지 않습니다. 모든 언어·조건까지 옮기려면 목록의 <strong>복제</strong>를 쓰세요.
            </p>
            <b-row class="mb-2">
                <b-col cols="8">
                    <b-input-group size="sm">
                        <b-form-input
                            v-model="copyModal.keyword"
                            placeholder="템플릿 이름 검색"
                            @keyup.enter="loadCopyList(1)"
                        />
                        <b-input-group-append>
                            <b-button variant="primary" size="sm" @click="loadCopyList(1)">검색</b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-col>
                <b-col cols="4" class="text-right">
                    <b-form-checkbox v-model="copyModal.sameScenarioOnly" class="small d-inline-block" @change="loadCopyList(1)">
                        같은 발송 상황만
                    </b-form-checkbox>
                </b-col>
            </b-row>
            <b-table
                :items="copyModal.list"
                :fields="copyModal.fields"
                small
                hover
                selectable
                select-mode="single"
                :busy="copyModal.loading"
                show-empty
                empty-text="템플릿이 없습니다."
                @row-selected="onCopyRowSelected"
            >
                <template #cell(scenario_code)="{ item }">
                    {{ scenarioLabel(item.scenario_code) }}
                </template>
                <template #cell(contexts)="{ item }">
                    <span v-if="item.is_fallback" class="text-muted">없음 (기본)</span>
                    <span v-else>
                        <b-badge v-for="(v, k) in item.contexts" :key="k" variant="light" class="mr-1">{{ v }}</b-badge>
                    </span>
                </template>
                <template #cell(locales)="{ item }">
                    <b-badge
                        v-for="locale in (item.locales || [])"
                        :key="locale"
                        :variant="locale === currentLocale ? 'primary' : 'light'"
                        class="mr-1"
                    >{{ locale }}</b-badge>
                </template>
                <template #table-busy>
                    <div class="text-center py-3"><b-spinner small /></div>
                </template>
            </b-table>
            <b-pagination
                v-if="copyModal.total > copyModal.perPage"
                v-model="copyModal.page"
                :total-rows="copyModal.total"
                :per-page="copyModal.perPage"
                size="sm"
                align="center"
                @change="loadCopyList"
            />
            <div class="mt-3 d-flex justify-content-end">
                <b-button
                    variant="primary"
                    :disabled="!copyModal.selected || copyModal.applying"
                    @click="applyCopy"
                >
                    <b-spinner v-if="copyModal.applying" small class="mr-1" />
                    이 템플릿 내용 가져오기
                </b-button>
            </div>
        </b-modal>

        <TestSendModal ref="testSendModal" />
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import SimpleContextBuilder from './_SubComponent/SimpleContextBuilder.vue'
import HtmlSplitEditor from './_SubComponent/HtmlSplitEditor.vue'
import VariableHelper from './_SubComponent/VariableHelper.vue'
import TestSendModal from './_SubComponent/TestSendModal.vue'
import MailDocCanvas from './_SubComponent/MailDocCanvas.vue'
import { LOCALE_OPTIONS, sortScenarios } from './data'
// ⛔ CommonJS 헬퍼는 이 코드베이스 관례대로 default import 로 받는다.
import templateCopy from './templateCopy'
import unsavedChangesGuard from '@shared/mixin/unsaved-changes-guard'

// 목록의 "복제" / 편집 화면의 "이 템플릿 복제" 가 새 템플릿 화면에 넘기는 원본 ID 쿼리 키.
const COPY_FROM_QUERY = 'copy_from'

function emptyForm() {
    return {
        label: '',
        scenario_code: '',
        isFallback: false,
        contexts: {},
        contents: [
            { locale: 'ko', is_default: true, subject: '', body: '' },
        ],
    }
}

export default {
    name: 'MailEditor',
    components: { SimpleContextBuilder, HtmlSplitEditor, VariableHelper, TestSendModal, MailDocCanvas },
    mixins: [unsavedChangesGuard],
    data() {
        return {
            loading: false,
            saving: false,
            form: emptyForm(),
            baseline: null, // 로드/저장 직후 폼 스냅샷 — 현재 폼과 다르면 미저장 변경
            justCreatedId: null,
            scenariosError: false,
            variablesError: false,
            scenarios: [],
            availableVariables: [],
            activeField: { type: 'body', index: 0 },
            activeTabIndex: 0,
            showAddLocale: false,
            newLocale: '',
            showVariables: false,
            previewRefreshKey: 0,
            canvasEpoch: 0, // 캔버스 강제 재생성(복사/리셋 시 ++, write-once 대응)
            copyModal: {
                show: false,
                keyword: '',
                // 일반 참여자용 템플릿을 매니저용에 가져오는 경우가 있어 기본은 전체.
                sameScenarioOnly: false,
                list: [],
                loading: false,
                page: 1,
                perPage: 10,
                total: 0,
                selected: null,
                applying: false,
                fields: [
                    { key: 'scenario_code', label: '발송 상황' },
                    { key: 'label', label: '템플릿 이름' },
                    { key: 'contexts', label: '조건' },
                    { key: 'locales', label: '언어' },
                ],
            },
        }
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id
        },
        templateId() {
            return this.$route.params.id ? parseInt(this.$route.params.id) : null
        },
        scenarioOptions() {
            const opts = [{ value: '', text: '시나리오 선택...' }]
            this.scenarios.forEach(s => {
                opts.push({ value: s.code, text: s.label || s.code })
            })
            return opts
        },
        availableLocaleOptions() {
            const used = this.form.contents.map(c => c.locale)
            return LOCALE_OPTIONS.filter(o => !used.includes(o.value))
        },
        currentLocale() {
            const c = this.form.contents[this.activeTabIndex]
            return c ? c.locale : ''
        },
        currentLocaleLabel() {
            return this.currentLocale ? this.getLocaleLabel(this.currentLocale) : ''
        },
        copyFromId() {
            const id = parseInt(this.$route.query[COPY_FROM_QUERY], 10)
            return Number.isFinite(id) ? id : null
        },
        // 미저장 변경 이탈 가드(unsaved-changes-guard 믹스인)가 읽는다.
        hasUnsavedChanges() {
            return !this.saving && this.baseline !== null && this.snapshotOf() !== this.baseline
        },
    },
    watch: {
        // 편집 화면에서 "이 템플릿 복제", 뒤로가기 등으로 :id / copy_from 만 바뀌면 같은 컴포넌트가
        // 재사용되므로(router-view 에 key 없음) 폼을 다시 채운다. 안 하면 URL 의 #5 에 다른 폼이 저장된다.
        '$route.fullPath'() {
            // 방금 생성해 replace 로 들어온 편집 URL — 폼이 이미 최신이다
            if (this.justCreatedId !== null && this.isEdit && this.templateId === this.justCreatedId) {
                this.justCreatedId = null
                return
            }
            this.justCreatedId = null
            this.initForm()
        },
    },
    mounted() {
        this.loadScenarios()
        this.initForm()
    },
    // 같은 레코드 안에서 :id / copy_from 만 바뀌는 이동(뒤로가기 등) — 미저장 편집이 있으면 확인
    beforeRouteUpdate(to, from, next) {
        this.confirmDiscardChanges().then((ok) => next(ok ? undefined : false))
    },
    methods: {
        snapshotOf() {
            const f = this.form
            return JSON.stringify([f.label, f.scenario_code, f.isFallback, f.contexts,
                f.contents.map(c => [c.locale, c.is_default, c.subject, c.body])])
        },
        markClean() {
            this.baseline = this.snapshotOf()
        },
        // 현재 라우트(:id / copy_from)에 맞춰 폼을 채운다.
        async initForm() {
            this.baseline = null
            if (this.isEdit) {
                await this.loadTemplate()
            } else if (this.copyFromId) {
                await this.loadDuplicateSource(this.copyFromId)
            } else {
                this.form = emptyForm()
                this.activeTabIndex = 0
                this.canvasEpoch++
                this.previewRefreshKey++
                this.markClean()
                await this.loadDefaultVariables()
            }
        },
        async loadScenarios() {
            this.scenariosError = false
            try {
                const res = await this.$axios.get('/mail/scenario')
                this.scenarios = sortScenarios(res.data || [])
            } catch (e) {
                console.error(e)
                this.scenariosError = true
                this.$EventBus.$emit('toast-failure', '조회 오류', '발송 상황 목록을 불러오지 못했습니다.')
            }
        },
        scenarioLabel(code) {
            const found = this.scenarios.find(s => s.code === code)
            return found ? (found.label || code) : code
        },
        async loadDefaultVariables() {
            this.variablesError = false
            try {
                const res = await this.$axios.get('/mail/template', { params: { page: 1, size: 1 } })
                const first = (res.data.results || [])[0]
                if (first) {
                    const full = await this.$axios.get(`/mail/template/${first.id}/full`)
                    this.availableVariables = full.data.available_variables || []
                }
            } catch (e) {
                console.error(e)
                this.variablesError = true
                this.$EventBus.$emit('toast-failure', '조회 오류', '사용 가능한 변수 목록을 불러오지 못했습니다.')
            }
        },
        async loadTemplate() {
            this.loading = true
            const id = this.templateId
            try {
                const res = await this.$axios.get(`/mail/template/${id}/full`)
                // 응답 전에 다른 템플릿으로 이동했으면 이 응답은 버린다
                if (id !== this.templateId) return
                const data = res.data

                this.form.label = data.label || ''
                this.form.scenario_code = data.scenario_code || ''
                this.form.contexts = data.contexts || {}
                this.form.isFallback = data.is_fallback || false
                this.availableVariables = data.available_variables || []

                this.form.contents = (data.contents || []).map(templateCopy.toEditorContent)

                if (this.form.contents.length === 0) {
                    this.form.contents.push({ locale: 'ko', is_default: true, subject: '', body: '', _sourceMode: false })
                }
                this.canvasEpoch++ // 로드 시 캔버스 재생성(새 body 반영)
                this.previewRefreshKey++
                this.markClean()
            } catch (e) {
                console.error(e)
                this.$EventBus.$emit('toast-failure', '오류', '템플릿을 불러올 수 없습니다.')
                this.goBack()
            } finally {
                this.loading = false
            }
        },
        // 템플릿 복제: 원본을 불러와 새 템플릿 폼을 모든 언어로 채운다(저장은 사용자가 '생성'을 눌러야).
        async loadDuplicateSource(id) {
            this.loading = true
            try {
                const res = await this.$axios.get(`/mail/template/${id}/full`)
                this.form = templateCopy.buildDuplicateForm(res.data)
                this.availableVariables = res.data.available_variables || []
                this.activeTabIndex = 0
                this.canvasEpoch++
                this.previewRefreshKey++
                this.markClean()
                this.$EventBus.$emit('toast-success', '복제',
                    `#${id} 템플릿을 복제했습니다. 발송 상황이나 조건을 바꾼 뒤 '생성'을 눌러야 저장됩니다.`)
            } catch (e) {
                console.error(e)
                this.$EventBus.$emit('toast-failure', '오류', '복제할 템플릿을 불러올 수 없습니다.')
            } finally {
                this.loading = false
            }
        },
        duplicateThis() {
            const query = { ...this.$route.query, [COPY_FROM_QUERY]: String(this.templateId) }
            this.$router.push({ name: 'MailEditorNew', query })
        },
        // 조건이 겹치는 템플릿이 있다. 작성 중인 내용을 잃지 않게 그 템플릿은 새 탭으로 연다.
        async showTemplateConflict(conflict) {
            if (conflict.id == null) {
                this.$EventBus.$emit('toast-warning', '중복', conflict.message)
                return
            }
            const h = this.$createElement
            const body = h('div', [
                h('p', { class: 'mb-2' }, conflict.message),
                conflict.detail ? h('pre', { class: 'small text-muted mb-0', style: 'white-space: pre-wrap' }, conflict.detail) : null,
            ])
            const open = await this.$bvModal.msgBoxConfirm([body], {
                title: '조건이 겹치는 템플릿', okTitle: `#${conflict.id} 템플릿 열기 (새 탭)`, cancelTitle: '닫기', centered: true,
            })
            if (!open) return
            const { href } = this.$router.resolve({ name: 'MailEditorEdit', params: { id: conflict.id } })
            window.open(href, '_blank', 'noopener')
        },
        getCanvas(idx) {
            const r = this.$refs[`canvas_${idx}`]
            return Array.isArray(r) ? r[0] : r
        },
        buildContentsPayload() {
            return this.form.contents.map((c, idx) => {
                // WYSIWYG 편집 중이면 캔버스에서 최신 저장용 HTML 을 당긴다(버튼 인라인화 포함).
                let body = c.body || ''
                if (!c._sourceMode) {
                    const canvas = this.getCanvas(idx)
                    if (canvas) body = canvas.getBodyText()
                }
                return {
                    locale: c.locale,
                    is_default: c.is_default,
                    subject_text: c.subject || '',
                    body_text: body,
                }
            })
        },
        // 불러온 조건을 건드리지 않고 저장하면 숫자가 섞여 있을 수 있다 → 값은 전부 문자열로.
        contextsPayload() {
            const out = {}
            Object.entries(this.form.contexts || {}).forEach(([k, v]) => { out[k] = String(v) })
            return out
        },
        // WYSIWYG 편집 중 변수(알약)가 손상된 로케일을 찾는다(저장 게이트).
        findTokenLossLocale() {
            for (let idx = 0; idx < this.form.contents.length; idx++) {
                const c = this.form.contents[idx]
                if (c._sourceMode) continue
                const canvas = this.getCanvas(idx)
                if (!canvas) continue
                const v = canvas.validateForSave()
                if (!v.ok) return c.locale
            }
            return null
        },
        // 캔버스 이벤트/모드 전환
        onCanvasInput(idx, bodyText) {
            const c = this.form.contents[idx]
            if (c) c.body = bodyText
        },
        onCanvasUnsupported(idx) {
            const c = this.form.contents[idx]
            if (!c) return
            // 콘텐츠 경계를 못 찾은 문서는 비주얼 편집이 위험 → HTML 직접 편집으로 강등.
            this.$set(c, '_sourceMode', true)
            this.$EventBus.$emit('toast-warning', '알림',
                `${this.getLocaleLabel(c.locale)} 본문은 구조를 자동 인식하지 못해 'HTML 직접 편집'으로 엽니다.`)
        },
        toggleSourceMode(idx) {
            const c = this.form.contents[idx]
            if (!c) return
            const wasClean = !this.hasUnsavedChanges
            // WYSIWYG → source 전환 시 캔버스의 최신 내용을 body 로 확정.
            if (!c._sourceMode) {
                const canvas = this.getCanvas(idx)
                if (canvas) c.body = canvas.getBodyText()
            } else {
                // source → WYSIWYG: 캔버스 재생성해 편집기 반영.
                this.canvasEpoch++
            }
            this.$set(c, '_sourceMode', !c._sourceMode)
            // 전환만으로 재직렬화된 본문은 편집이 아니다 — 전환 전에 깨끗했으면 기준 갱신
            if (wasClean) this.markClean()
        },
        async save() {
            if (!this.form.scenario_code) {
                this.$EventBus.$emit('toast-warning', '알림', '발송 상황(시나리오)을 선택해주세요.')
                return
            }
            if (!this.form.label) {
                this.$EventBus.$emit('toast-warning', '알림', '템플릿 이름을 입력해주세요.')
                return
            }
            // 조건 게이트: 조건을 다 지운 채 저장하면 기본 템플릿이 하나 더 생긴다.
            if (!this.form.isFallback) {
                const builder = this.$refs.contextBuilder
                if (builder && builder.hasIncompleteRow()) {
                    this.$EventBus.$emit('toast-failure', '조건 오류', '값을 고르지 않은 조건이 있습니다')
                    return
                }
                if (!Object.keys(this.form.contexts || {}).length) {
                    this.$EventBus.$emit('toast-failure', '조건 오류', '조건을 하나 이상 넣거나 \'기본 (조건 없음)\'을 선택하세요')
                    return
                }
            }
            // 저장 게이트: WYSIWYG 편집 중 변수(알약)가 깨졌으면 저장 차단(발송 시 이름 누락 방지).
            const lossLocale = this.findTokenLossLocale()
            if (lossLocale) {
                this.$EventBus.$emit('toast-failure', '변수 손상',
                    `${this.getLocaleLabel(lossLocale)} 본문의 변수가 손상되었습니다. 'HTML 직접 편집'으로 확인 후 다시 저장해주세요.`)
                return
            }

            this.saving = true
            try {
                const payload = {
                    scenario_code: this.form.scenario_code,
                    label: this.form.label,
                    contexts: this.form.isFallback ? {} : this.contextsPayload(),
                    contents: this.buildContentsPayload(),
                }

                if (this.isEdit) {
                    await this.$axios.patch(`/mail/template/quick/${this.templateId}`, payload)
                    this.$EventBus.$emit('toast-success', '완료', '저장되었습니다.')
                    await this.loadTemplate()
                } else {
                    const res = await this.$axios.post('/mail/template/quick', payload)
                    this.$EventBus.$emit('toast-success', '완료', '템플릿이 생성되었습니다.')
                    this.justCreatedId = res.data.id
                    this.markClean()
                    this.$router.replace({ name: 'MailEditorEdit', params: { id: res.data.id } })
                }
            } catch (e) {
                const status = e.response?.status
                const data = e.response?.data
                const conflict = templateCopy.parseTemplateConflict(status, data)
                if (conflict) {
                    this.showTemplateConflict(conflict)
                    return
                }
                const msg = typeof data === 'object' ? (data?.error || data?.message || '저장에 실패했습니다.') : (data || '저장에 실패했습니다.')
                if (status === 422) {
                    // 백엔드가 flat 문서 전체의 {{ }} 변수를 재검증한다. 유효하지 않은 변수가
                    // 있으면 그 원인을 그대로 보여준다(사용자가 못 고치는 공유 토큰일 수 있음).
                    this.$EventBus.$emit('toast-warning', '변수 오류',
                        `저장할 수 없는 변수가 있습니다: ${msg}`)
                } else if (status === 403) {
                    this.$EventBus.$emit('toast-failure', '권한 부족', '이 작업을 수행할 권한이 없습니다.')
                } else {
                    this.$EventBus.$emit('toast-failure', '오류', msg)
                }
            } finally {
                this.saving = false
            }
        },
        async deleteTemplate() {
            const confirmed = await this.$bvModal.msgBoxConfirm('이 템플릿을 삭제하시겠습니까?', {
                title: '템플릿 삭제', okVariant: 'danger', okTitle: '삭제', cancelTitle: '취소', centered: true,
            })
            if (!confirmed) return
            try {
                await this.$axios.delete(`/mail/template/${this.templateId}`)
                this.$EventBus.$emit('toast-success', '완료', '템플릿이 삭제되었습니다.')
                this.markClean()
                this.goBack()
            } catch (e) {
                const msg = getErrorMessage(e, '삭제에 실패했습니다.')
                this.$EventBus.$emit('toast-failure', '오류', msg)
            }
        },

        // ── 변수 삽입 ──────────────────────────────
        setActiveField(type, index) {
            this.activeField = { type, index }
        },
        insertVariable(key, field) {
            // 본문(제목이 아닌 경우): 활성 탭이 WYSIWYG 면 캔버스에 알약으로 삽입.
            if (this.activeField.type !== 'subject') {
                const idx = this.activeTabIndex
                const content = this.form.contents[idx]
                if (content && !content._sourceMode) {
                    const canvas = this.getCanvas(idx)
                    if (canvas) { canvas.insertVariable(`{{ reserve ${key}.${field} }}`, `${key}.${field}`); return }
                }
            }
            // 제목, 또는 HTML 직접 편집(source) 본문: 기존 textarea/input 문자열 삽입.
            const { type, index } = this.activeField
            const content = this.form.contents[index]
            if (!content) return
            const varText = `{{ reserve ${key}.${field} }}`
            const prop = type === 'subject' ? 'subject' : 'body'
            const refName = `${type}_${index}`
            const refEl = this.$refs[refName]
            const comp = Array.isArray(refEl) ? refEl[0] : refEl
            const el = comp && (
                typeof comp.getTextareaElement === 'function'
                    ? comp.getTextareaElement()
                    : (comp.$el || comp)
            )
            if (!el || el.selectionStart == null) {
                content[prop] = (content[prop] || '') + varText
                this.previewRefreshKey++
                return
            }
            const start = el.selectionStart
            const end = el.selectionEnd
            const text = content[prop] || ''
            content[prop] = text.substring(0, start) + varText + text.substring(end)
            this.$nextTick(() => {
                const pos = start + varText.length
                el.setSelectionRange(pos, pos)
                el.focus()
            })
            this.previewRefreshKey++
        },

        // ── 내용 가져오기 (현재 언어 탭만) ────────────
        openCopyModal() {
            this.copyModal.selected = null
            this.copyModal.keyword = ''
            this.copyModal.show = true
            this.loadCopyList(1)
        },
        async loadCopyList(page = 1) {
            this.copyModal.loading = true
            try {
                const params = { page, size: this.copyModal.perPage }
                if (this.copyModal.keyword) params.keyword = this.copyModal.keyword
                if (this.copyModal.sameScenarioOnly && this.form.scenario_code) {
                    params.scenario = this.form.scenario_code
                }
                const res = await this.$axios.get('/mail/template', { params })
                this.copyModal.list = res.data.results || []
                this.copyModal.total = res.data.total || 0
                this.copyModal.page = page
            } catch (e) {
                this.copyModal.list = []
            } finally {
                this.copyModal.loading = false
            }
        },
        onCopyRowSelected(rows) {
            this.copyModal.selected = rows.length ? rows[0] : null
        },
        async applyCopy() {
            if (!this.copyModal.selected) return
            this.copyModal.applying = true
            try {
                const res = await this.$axios.get(`/mail/template/${this.copyModal.selected.id}/full`)
                const cur = this.form.contents[this.activeTabIndex]
                if (!cur) return
                const pick = templateCopy.pickSameLocale(res.data.contents, cur.locale)
                if (!pick) {
                    this.$EventBus.$emit('toast-warning', '가져올 수 없음',
                        `원본 템플릿에 ${this.getLocaleLabel(cur.locale)} 본문이 없습니다. 다른 템플릿을 고르거나 언어 탭을 바꾸세요.`)
                    return
                }
                const picked = templateCopy.toEditorContent(pick)
                this.$set(cur, 'subject', picked.subject)
                this.$set(cur, 'body', picked.body)
                this.canvasEpoch++ // 복사한 body 를 캔버스에 반영(write-once → 재생성)
                this.previewRefreshKey++
                this.copyModal.show = false
                this.$EventBus.$emit('toast-success', '완료', '내용을 가져왔습니다. 자유롭게 편집하세요.')
            } catch (e) {
                this.$EventBus.$emit('toast-failure', '오류', '내용을 가져오지 못했습니다.')
            } finally {
                this.copyModal.applying = false
            }
        },

        // ── Locale 관리 ────────────────────────────
        addLocale() {
            if (!this.newLocale) return
            this.form.contents.push({
                locale: this.newLocale,
                is_default: false,
                subject: '',
                body: '',
                _sourceMode: false,
            })
            this.newLocale = ''
            this.$nextTick(() => {
                this.activeTabIndex = this.form.contents.length - 1
            })
        },
        removeContent(idx) {
            if (this.form.contents.length <= 1) {
                this.$EventBus.$emit('toast-warning', '알림', '최소 1개의 언어가 필요합니다.')
                return
            }
            this.form.contents.splice(idx, 1)
            if (this.activeTabIndex >= this.form.contents.length) {
                this.activeTabIndex = this.form.contents.length - 1
            }
        },
        getLocaleLabel(locale) {
            const found = LOCALE_OPTIONS.find(o => o.value === locale)
            return found ? found.text : locale
        },
        openTestSend() {
            this.$refs.testSendModal.show(this.templateId, {
                availableVariables: this.availableVariables,
                contents: this.form.contents.map(c => ({
                    locale: c.locale,
                    subject_text: c.subject || '',
                    body_text: c.body || '',
                })),
            })
        },
        goBack() {
            const query = { ...(this.$route.query || {}) }
            delete query[COPY_FROM_QUERY]
            this.$router.push({ name: 'MailDashboard', query })
        },
    },
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
.mail-editor-page {
    max-width: 1600px;
}
</style>
